import { useTranslator } from '@bluebeam/react-i18n';
import cc from 'classcat';
import EditLayerDropdown from 'components/EditLayerDropdown';
import InvisibleEye from 'components/Icons/InvisibleEye';
import VisibleEye from 'components/Icons/VisibleEye';
import { Button, Modal, ModalContent, ModalFooter } from 'components/lib';
import { useMapLayersManager } from 'providers/MapLayersManagerProvider';
import { useProjectManager } from 'providers/ProjectManagerProvider';
import React, { useState } from 'react';
import { SheetVersion } from 'records/SheetVersion';

type LayerListItemProps = {
  allowItemEdit: boolean;
  sheetVersion: SheetVersion;
  visible: boolean;
  onVisibilityChange: (visible: boolean) => void;
};
const LayerListItem: React.FC<LayerListItemProps> = ({
  allowItemEdit,
  sheetVersion,
  visible,
  onVisibilityChange,
}: LayerListItemProps) => {
  const { translate } = useTranslator([
    'mapView',
    'buttons',
    'disciplineFilterSection',
  ]);
  const projectManager = useProjectManager();
  const mapLayersManager = useMapLayersManager();
  const { permissions } = projectManager.getSelectedProject();
  const [isModalShown, setIsModalShown] = useState<boolean>(false);

  const deleteModal = (
    <Modal
      size="small"
      title={translate('mapView:deleteMapLayerFields.headerText')}
      onDismiss={() => setIsModalShown(false)}
    >
      <ModalContent>
        <p>{translate('mapView:deleteMapLayerFields.descriptionText')}</p>
        <p className="bold">
          {translate('mapView:deleteMapLayerFields.confirmChoiceText')}
        </p>
      </ModalContent>
      <ModalFooter>
        <Button
          id="delete-map-layer-deny-btn"
          onClick={() => setIsModalShown(false)}
          variant="tertiary"
          size="large"
        >
          {translate('buttons:cancel')}
        </Button>
        <Button
          id="delete-map-layer-accept-btn"
          variant="destructive"
          onClick={() => {
            mapLayersManager.removeMappedSheetVersion(sheetVersion.pageid);
            setIsModalShown(false);
          }}
          size="large"
        >
          {translate('buttons:delete')}
        </Button>
      </ModalFooter>
    </Modal>
  );

  const onDeleteClick = () => setIsModalShown(true);

  const onEditClick = async () => {
    mapLayersManager.openPrepareDrawingModal(sheetVersion);
  };

  const discipline: `${string}:${string}` =
    `disciplineFilterSection:disciplines.${sheetVersion.getIn([
      'sheetDiscipline',
      'description',
    ])}` as `${string}:${string}`;

  return (
    <>
      {isModalShown && deleteModal}
      <li
        data-testid="map-layer-row"
        className="bb-3 row bgd-white-2 p4 justify-between"
      >
        <div
          id={`map-layer-toggle-${sheetVersion.pageid}`}
          className="row items-center"
          onClick={() => onVisibilityChange(!visible)}
        >
          <div className="shrink-0">
            {visible ? <VisibleEye /> : <InvisibleEye />}
          </div>
          <div className="pl3 black-2">
            <h3
              className={cc([
                `pb1 bold font-size-5 nowrap oxh line-height-3 ellipsis`,
                { 'primary-blue bold': visible },
              ])}
              style={{
                width: '300px',
              }}
            >
              {sheetVersion.sheettitle}
            </h3>
            <span className="line-height-2 font-size-5">
              <span className="medium black-1">{sheetVersion.sheetnumber}</span>
            </span>
          </div>
        </div>
        {allowItemEdit && permissions.createMapLayer && (
          <div className="row items-center">
            <EditLayerDropdown
              onDeleteClick={() => onDeleteClick()}
              onEditClick={() => onEditClick()}
            />
          </div>
        )}
      </li>
    </>
  );
};

export default LayerListItem;
